import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

// Données de protection sociale par statut
const PROTECTION_SOCIALE = {
  chomage: {
    titre: '💼 Assurance chômage',
    cae: { niveau: 'complete', texte: 'Couvert', symbole: '✅' },
    micro: { niveau: 'aucune', texte: 'Aucune', symbole: '❌' },
    ei: { niveau: 'aucune', texte: 'Aucune', symbole: '❌' },
    sasu: { niveau: 'aucune', texte: 'Aucune', symbole: '❌' },
    details: {
      cae: [
        '✅ Droit à l\'indemnisation par France Travail',
        '✅ Cotisations versées par l\'employeur (la CAE) et payées par l\'activité',
        '✅ Droits calculés selon l\'historique des salaires',
        '✅ Montant : jusqu\'à 75% du salaire net',
        '✅ Cumul emploi / ARE possible',
        '✅ CSP (Contrat de Sécurisation Professionnelle) possible',
        '❌ ACRE non possible',
      ],
      autres: [
        '❌ Pas de couverture',
        '⚠️ Possibilité de souscrire une assurance privée (coût supplémentaire)',
        '✅ Dispositif ACRE et ARCE pour les créateurs d\'entreprise (sous conditions)',
      ],
    },
  },
  retraite: {
    titre: '👴 Retraite',
    cae: { niveau: 'complete', texte: 'Complète (base + complémentaire)', symbole: '✅' },
    micro: { niveau: 'partielle', texte: 'Limitée (base seule)', symbole: '⚠️' },
    ei: { niveau: 'partielle', texte: 'Limitée (base)', symbole: '⚠️' },
    sasu: { niveau: 'bonne', texte: 'Bonne (base + complémentaire)', symbole: '✅' },
    details: {
      cae: [
        '✅ Retraite de base + complémentaire (AGIRC-ARRCO)',
        '✅ Cotisations et droits plus élevés',
        '✅ Retraite possible en CAE sous réserve de validation des trimestres',
        '✅ Trimestre validé dès 50h travaillées au SMIC horaire/mois',
      ],
      micro: [
        '⚠️ Retraite de base et retraite complémentaire obligatoire',
        '⚠️ Cotisations et droits plus faibles',
        '⚠️ Trimestre difficile à valider (basés sur le chiffre d\'affaires)',
      ],
      ei: [
        '⚠️ Retraite de base + complémentaire limitée',
        '⚠️ Cotisations moyennes',
      ],
      sasu: [
        '✅ Retraite de base + complémentaire (comme salarié)',
        '✅ Cotisations et droits plus élevés',
      ],
    },
  },
  at: {
    titre: '🏥 Accident du travail',
    cae: { niveau: 'complete', texte: 'Couvert', symbole: '✅' },
    micro: { niveau: 'partielle', texte: 'Non couvert', symbole: '⚠️' },
    ei: { niveau: 'partielle', texte: 'Non couvert', symbole: '⚠️' },
    sasu: { niveau: 'partielle', texte: 'Non couvert', symbole: '⚠️' },
    details: {
      cae: [
        '✅ Protection complète en cas d\'accident du travail',
        '✅ Droits aux indemnités journalières selon résultat',
        '✅ Prise en charge des frais médicaux',
      ],
      autres: [
        '⚠️ Pas de couverture automatique',
        '⚠️ Assurance privée recommandée',
      ],
    },
  },
  maladie_pro: {
    titre: '🩺 Maladie professionnelle',
    cae: { niveau: 'complete', texte: 'Reconnue', symbole: '✅' },
    micro: { niveau: 'partielle', texte: 'Non reconnue', symbole: '⚠️' },
    ei: { niveau: 'partielle', texte: 'Non reconnue', symbole: '⚠️' },
    sasu: { niveau: 'partielle', texte: 'Non reconnue', symbole: '⚠️' },
    details: {
      cae: [
        '✅ Reconnaissance des maladies professionnelles',
        '✅ Indemnisation selon résultat',
        '✅ Suivi médical',
      ],
      autres: [
        '⚠️ Difficultés de reconnaissance',
        '⚠️ Pas d\'indemnisation automatique',
      ],
    },
  },
};

// Composant ligne de tableau
function TableRow({ critere, donnees, statutComparaison, onToggle, isOpen }) {
  const getCouleur = (niveau) => {
    switch (niveau) {
      case 'complete':
        return 'text-green-600 bg-green-50';
      case 'bonne':
        return 'text-green-600 bg-green-50';
      case 'partielle':
        return 'text-orange-600 bg-orange-50';
      case 'aucune':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <>
      <tr className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
        {/* Critère */}
        <td className="px-4 py-4 font-medium text-gray-900 sticky left-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <span>{donnees.titre}</span>
            <button
              onClick={() => onToggle(critere)}
              className="text-primary-600 hover:text-primary-700 focus:outline-none"
              aria-label="En savoir plus"
            >
              <svg 
                className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </td>

        {/* CAE */}
        <td className={`px-4 py-4 text-center ${getCouleur(donnees.cae.niveau)}`}>
          <div className="flex flex-col items-center gap-1">
            <span className="text-2xl">{donnees.cae.symbole}</span>
            <span className="text-sm font-medium">{donnees.cae.texte}</span>
          </div>
        </td>

        {/* Statut de comparaison */}
        <td className={`px-4 py-4 text-center ${getCouleur(donnees[statutComparaison].niveau)}`}>
          <div className="flex flex-col items-center gap-1">
            <span className="text-2xl">{donnees[statutComparaison].symbole}</span>
            <span className="text-sm font-medium">{donnees[statutComparaison].texte}</span>
          </div>
        </td>
      </tr>

      {/* Détails (accordéon) */}
      {isOpen && (
        <tr className="bg-blue-50 border-b border-blue-200">
          <td colSpan="3" className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* CAE */}
              <div>
                <h4 className="font-bold text-primary-700 mb-3">🟢 CAE (Salarié·e)</h4>
                <ul className="space-y-2">
                  {donnees.details.cae.map((detail, index) => (
                    <li key={index} className="text-sm text-gray-700">{detail}</li>
                  ))}
                </ul>
              </div>

              {/* Autre statut */}
              <div>
                <h4 className="font-bold text-blue-700 mb-3">
                  {statutComparaison === 'micro' && '📱 Micro-entreprise'}
                  {statutComparaison === 'ei' && '👤 Entreprise Individuelle'}
                  {statutComparaison === 'sasu' && '🏢 SASU'}
                </h4>
                <ul className="space-y-2">
                  {(donnees.details[statutComparaison] || donnees.details.autres).map((detail, index) => (
                    <li key={index} className="text-sm text-gray-700">{detail}</li>
                  ))}
                </ul>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

TableRow.propTypes = {
  critere: PropTypes.string.isRequired,
  donnees: PropTypes.object.isRequired,
  statutComparaison: PropTypes.string.isRequired,
  onToggle: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
};

// Composant principal Step3
function Step3ProtectionSociale({ data, onPrevious, onNext }) {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [accordeonsOuverts, setAccordeonsOuverts] = useState({});

  const toggleAccordeon = (critere) => {
    setAccordeonsOuverts(prev => ({
      ...prev,
      [critere]: !prev[critere],
    }));
  };

  const statutComparaison = data.statutComparaison || 'micro';

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white rounded-xl shadow-card p-6 md:p-8">
        {/* En-tête */}
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            🛡️ Comparaison de la protection sociale
          </h2>
          <p className="text-gray-600">
            Comparez la couverture sociale entre la CAE et le statut {' '}
            {statutComparaison === 'micro' && 'Micro-entreprise'}
            {statutComparaison === 'ei' && 'Entreprise Individuelle'}
            {statutComparaison === 'sasu' && 'SASU'}
          </p>
        </div>

        {/* Tableau comparatif */}
        <div className="mb-8 overflow-x-auto">
          <table className="w-full border-collapse border border-gray-200 rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
                <th className="px-4 py-4 text-left font-bold sticky left-0 bg-primary-600 z-20">
                  Critère
                </th>
                <th className="px-4 py-4 text-center font-bold min-w-[180px]">
                  🟢 CAE
                </th>
                <th className="px-4 py-4 text-center font-bold min-w-[180px]">
                  {statutComparaison === 'micro' && '📱 Micro-entreprise'}
                  {statutComparaison === 'ei' && '👤 EI'}
                  {statutComparaison === 'sasu' && '🏢 SASU'}
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(PROTECTION_SOCIALE).map(([critere, donnees]) => (
                <TableRow
                  key={critere}
                  critere={critere}
                  donnees={donnees}
                  statutComparaison={statutComparaison}
                  onToggle={toggleAccordeon}
                  isOpen={!!accordeonsOuverts[critere]}
                />
              ))}
            </tbody>
          </table>
        </div>

        {/* Légende */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
          <h3 className="font-bold text-gray-900 mb-4">📖 Légende</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3">
              <span className="text-2xl">✅</span>
              <span className="text-sm text-gray-700">Protection complète et garantie</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-2xl">⚠️</span>
              <span className="text-sm text-gray-700">Protection partielle ou limitée</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-2xl">❌</span>
              <span className="text-sm text-gray-700">Aucune protection</span>
            </div>
          </div>
        </div>

        {/* Message important */}
        <div className="bg-primary-50 border-l-4 border-primary-500 rounded-r-lg p-6 mb-8">
          <div className="flex items-start gap-4">
            <svg className="w-6 h-6 text-primary-600 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <h3 className="font-bold text-primary-900 mb-2">
                💡 Point clé : L'assurance chômage fait toute la différence (sous conditions)
              </h3>
              <p className="text-sm text-primary-800">
                En CAE, vous êtes salarié·e et bénéficiez donc de l'assurance chômage avec un niveau de protection sociale plus élevé. 
                En cas d'arrêt d'activité, vous pouvez percevoir jusqu'à 75% de votre salaire net pendant 
                plusieurs mois. Les autres statuts (Micro, EI, SASU) ne bénéficient pas de cette protection, 
                sauf souscription d'une assurance privée entrainant des coûts supplémentaires.
              </p>
              <p className="text-sm text-primary-800 mt-2">
                Vous bénéficiez d'un accompagnement dans les démarches administratives et dans le développement de votre activité.
              </p>
            </div>
          </div>
        </div>

        {/* Boutons de navigation */}
        <div className="flex flex-col sm:flex-row justify-between gap-4 pt-6 border-t border-gray-200">
          <button
            onClick={onPrevious}
            className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>Retour aux résultats</span>
          </button>

          <button
            onClick={onNext}
            className="px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-bold text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-3"
          >
            <span>Imprimer ces résultats</span>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
        </div>
      </div>

      {/* Note d'aide */}
      <div className="mt-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
        <div className="flex items-start gap-3">
          <svg className="w-6 h-6 text-yellow-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="flex-1">
            <p className="text-sm font-medium text-yellow-800">
              💡 Astuce : Cliquez sur les flèches (▼) pour voir les détails de chaque protection
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

Step3ProtectionSociale.propTypes = {
  data: PropTypes.object.isRequired,
  onPrevious: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
};

export default Step3ProtectionSociale;
